/**
 * imageSizing — le dimensionnement d'une image PAR POINT DE RUPTURE.
 *
 * Une valeur `image-drupal` peut porter une clé `sizing` en plus de son `mid` :
 *
 *   cover:
 *     mid: 412
 *     alt: "Vue de l'atelier"
 *     sizing:
 *       op: sc                      # style de cadrage (l'engrenage)
 *       ext: webp                   # format de sortie
 *       desktop: { w: 640, h: 400 }
 *       tablet:  { w: 460, h: 345 }
 *       mobile:  { w: 375, h: 500 }
 *       focus:   { x: 25, y: 80 }    # le point d'intérêt (le glissé dans le cadre)
 *
 * Elle est écrite par la poignée de redimensionnement du canevas et par
 * l'éditeur `image-sizing`, et lue par le primitif `LazyImage`. Elle vit DANS la
 * valeur de l'image, pas à côté : `OrbitDataEnricher::stripValue()` ne retire
 * que `url/width/height/style/src/scheme/cropBase` d'un champ `image-drupal`,
 * donc `sizing` survit au round-trip de sauvegarde — y compris dans une colonne
 * de liste, où le strip recurse par schéma.
 *
 * Deux choses en découlent, et ce module porte les deux :
 *  - la BOÎTE affichée, appliquée en CSS par bande de conteneur (variables
 *    personnalisées + recettes `.builder-img-sized` d'index.css) ;
 *  - la DÉRIVÉE téléchargée (`widths` / `sizes`, et un `<picture>` quand les
 *    rapports de forme diffèrent d'une bande à l'autre).
 *
 * Aucune valeur par défaut n'est inventée : sans `sizing`, `LazyImage` garde
 * exactement le comportement déclaré dans le YAML du bloc. C'est ce qui rend la
 * fonction rétrocompatible avec les ~80 blocs déjà écrits.
 */

import { STEP, MAX_DIMENSION, FOCUS_STEP, quantize, normalizeFocus } from '../../../utils/cropUrl'
import { BAND_MAX_WIDTHS, RESPONSIVE_DEVICES } from './responsiveVisibility'

/** Le pas de la poignée = celui du quantizer d'URL : un cran = une dérivée. */
export const SIZING_STEP = STEP
/** Le pas du point d'intérêt, en pourcents (partagé avec le serveur). */
export { FOCUS_STEP }
export const MIN_BOX = STEP
export const MAX_BOX = MAX_DIMENSION

/**
 * Les styles de cadrage exposés à l'éditeur. Ce sont les ops du module
 * `dynamic_image` (`CropParamsNormalizer::CROP_OPS`), moins `f` (focal) : le
 * module `focal_point` n'est pas activé, `f` y retombe silencieusement sur `sc`
 * — proposer un choix sans effet serait mentir à l'éditeur.
 */
export const CROP_OPS = [
  { id: 'sc', label: 'Couvrir', hint: 'Remplit la boîte, recadre le débord' },
  { id: 'c', label: 'Rogner', hint: 'Découpe la boîte, sans mise à l’échelle' },
  { id: 's', label: 'Contenir', hint: 'Met à l’échelle, garde toute l’image' },
]

/**
 * LE POINT D'INTÉRÊT — « où, dans l'image, tombe le cadre ».
 *
 * Un cadrage qui DÉCOUPE jette une partie de l'image ; par défaut le serveur
 * jette autour du centre, ce qui décapite les portraits et coupe les logos. Le
 * point d'intérêt dit quelle part du DÉBORD reste visible : `0` colle le bord
 * gauche (ou haut) au cadre, `100` le bord droit (ou bas), `50` centre — donc
 * exactement le comportement d'avant, et c'est pourquoi un point centré n'est
 * jamais stocké ni encodé (cf. `normalizeFocus` dans cropUrl.js : la dérivée
 * déjà générée reste valable, rien n'est à regénérer).
 *
 * Il vaut pour TOUTES les bandes : une photo se cadre sur son sujet, et le
 * sujet ne change pas parce que l'écran rétrécit. Ce sont les BOÎTES qui
 * varient par bande, pas le point.
 */
export const CENTERED_FOCUS = { x: 50, y: 50 }

/** Un cadrage qui découpe — le seul cas où viser quelque chose a un sens. */
export const supportsFocus = (op) => op !== 's'

/**
 * Les neuf ancrages classiques, dans l'ordre de lecture d'une grille 3×3.
 *
 * Ce sont les positions qu'on veut dans 90 % des cas — « garde le haut » sur un
 * portrait, « garde la gauche » sur un logo — et les seules qu'on puisse
 * atteindre d'un clic sûr : viser 0 ou 100 au glissé demande d'aller buter
 * exactement en bout de course.
 */
export const FOCUS_ANCHORS = [
  { id: 'top-left', x: 0, y: 0, label: 'Haut gauche' },
  { id: 'top', x: 50, y: 0, label: 'Haut' },
  { id: 'top-right', x: 100, y: 0, label: 'Haut droite' },
  { id: 'left', x: 0, y: 50, label: 'Gauche' },
  { id: 'center', x: 50, y: 50, label: 'Centre' },
  { id: 'right', x: 100, y: 50, label: 'Droite' },
  { id: 'bottom-left', x: 0, y: 100, label: 'Bas gauche' },
  { id: 'bottom', x: 50, y: 100, label: 'Bas' },
  { id: 'bottom-right', x: 100, y: 100, label: 'Bas droite' },
]

export const OUTPUT_FORMATS = [
  { id: 'webp', label: 'WebP' },
  { id: 'avif', label: 'AVIF' },
]

/**
 * Le style de cadrage d'une boîte qui n'en déclare pas — donc celui que la
 * POIGNÉE écrit quand elle crée un dimensionnement.
 *
 * « Contenir » et non « Couvrir » : tirer une poignée veut dire AGRANDIR ou
 * RÉDUIRE, jamais recadrer. Avec `sc`, la boîte est honorée au pixel près et
 * l'image est rognée dès que le rapport dérive — et il dérive toujours un peu,
 * puisque chaque côté est calé sur le pas de 20 px. Un logo y perdait ses bords
 * sans que rien ne le signale. Le recadrage reste à un clic, dans l'engrenage.
 *
 * Les dimensionnements DÉJÀ enregistrés portent tous un `op` explicite
 * (`normalizeSizing` en écrit un) : ce défaut ne change qu'à la création.
 */
export const DEFAULT_OP = 's'

/**
 * Les bandes, dans l'ordre où une condition de média est évaluée : la première
 * qui matche gagne, le desktop est le défaut sans condition.
 *
 * Elles sont DÉRIVÉES de `BAND_MAX_WIDTHS` (`responsiveVisibility.js`, qui se
 * déclare source unique des bandes) : recopier les seuils ici en aurait fait
 * une troisième table à tenir d'accord, après celle du module et celle des
 * `@container` d'index.css.
 */
export const SIZING_BANDS = Object.entries(BAND_MAX_WIDTHS)
  .map(([device, max]) => ({ device, media: `(max-width: ${max}px)` }))

/** Ordre de repli d'une bande non réglée : on hérite du plus large réglé. */
const FALLBACK_ORDER = {
  mobile: ['mobile', 'tablet', 'desktop'],
  tablet: ['tablet', 'desktop', 'mobile'],
  desktop: ['desktop', 'tablet', 'mobile'],
}

/** Une boîte valide, ou null. Les deux côtés sont requis : c'est un rapport. */
export const normalizeBox = (box) => {
  if (!box || typeof box !== 'object') return null
  const w = Math.round(Number(box.w))
  const h = Math.round(Number(box.h))
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null
  return {
    w: Math.min(MAX_BOX, Math.max(MIN_BOX, w)),
    h: Math.min(MAX_BOX, Math.max(MIN_BOX, h)),
  }
}

/** La valeur `sizing` d'une image, nettoyée — ou null si elle ne règle rien. */
export const normalizeSizing = (sizing) => {
  if (!sizing || typeof sizing !== 'object') return null
  const out = {}
  let any = false
  for (const device of RESPONSIVE_DEVICES) {
    const box = normalizeBox(sizing[device])
    if (box) {
      out[device] = box
      any = true
    }
  }
  // Un style de cadrage DÉCLARÉ est un réglage, même sans boîte : sans ça,
  // choisir « Couvrir » sur une image que le bloc dimensionne lui-même en YAML
  // rendait null — le bouton revenait à sa valeur d'avant, sans erreur, et le
  // sélecteur paraissait mort.
  const declaredOp = CROP_OPS.some(o => o.id === sizing.op) ? sizing.op : null
  const op = declaredOp ?? DEFAULT_OP
  out.op = op
  if (declaredOp) any = true
  const focus = normalizeFocus(sizing.focus, op)
  if (focus) {
    // Un point d'intérêt SEUL est un réglage lui aussi : une image dont le bloc
    // déclare déjà la boîte n'a pas de bande réglée, et on veut pouvoir la
    // recadrer quand même.
    out.focus = focus
    any = true
  }
  if (OUTPUT_FORMATS.some(f => f.id === sizing.ext)) {
    out.ext = sizing.ext
    any = true
  }
  if (typeof sizing.lockRatio === 'boolean') out.lockRatio = sizing.lockRatio
  return any ? out : null
}

/** Le `sizing` d'une valeur d'image, quelle que soit sa forme. */
export const sizingOf = (value) => (
  value && typeof value === 'object' ? normalizeSizing(value.sizing) : null
)

/** La boîte d'une bande, repli compris. Null si rien n'est réglé. */
export const resolveBox = (sizing, device) => {
  const normalized = normalizeSizing(sizing)
  if (!normalized) return null
  for (const candidate of (FALLBACK_ORDER[device] ?? FALLBACK_ORDER.desktop)) {
    if (normalized[candidate]) return normalized[candidate]
  }
  return null
}

/**
 * Les trois bandes résolues d'un coup. Null si AUCUNE boîte n'est réglée — un
 * `sizing` qui ne porte qu'un point d'intérêt en est un : rendre un objet aux
 * trois bandes nulles ferait croire à l'appelant qu'il a une boîte, et
 * `boxes.desktop.w` planterait l'écran.
 */
export const resolvedBoxes = (sizing) => {
  const normalized = normalizeSizing(sizing)
  if (!normalized) return null
  const out = {}
  for (const device of RESPONSIVE_DEVICES) {
    const box = resolveBox(normalized, device)
    if (!box) return null
    out[device] = box
  }
  return out
}

/**
 * Le point d'intérêt réglé, ou null quand l'image est cadrée au centre (le
 * défaut). `focusPoint` en donne toujours un, pour amorcer un geste.
 */
export const focusOf = (sizing) => normalizeSizing(sizing)?.focus ?? null

/** Le point de départ d'un geste : celui qui est réglé, sinon le centre. */
export const focusPoint = (sizing) => focusOf(sizing) ?? CENTERED_FOCUS

/** La valeur `object-position` d'un point — c'est la MÊME arithmétique que la
 * découpe du serveur : un pourcentage du débord, pas de l'image. */
export const focusPosition = (focus) => `${focus.x}% ${focus.y}%`

/**
 * `sizing` avec un autre point d'intérêt (centré = on retire le réglage).
 *
 * `op` est le style de cadrage EFFECTIF, celui que l'image rend réellement :
 * quand rien n'est encore réglé, il vient du YAML du bloc (le canevas le lit
 * sur l'élément, cf. `data-builder-op`) et non du défaut de la poignée — sans
 * lui, viser une image jamais redimensionnée écrirait un point sous « Contenir »,
 * qui n'en veut pas, et le réglage disparaîtrait sans erreur.
 */
export const setFocus = (sizing, focus, op) => {
  const next = { ...(sizing && typeof sizing === 'object' ? sizing : {}) }
  if (!next.op) next.op = op ?? DEFAULT_OP
  const point = normalizeFocus(focus, next.op)
  if (point) next.focus = point
  else delete next.focus
  return normalizeSizing(next)
}

/**
 * La LARGEUR d'une dérivée qui COUVRE la boîte sans rien jeter : celle qu'on
 * sert pendant l'édition, pour que le glissé se voie tout de suite.
 *
 * En édition, cadrer côté serveur ne montrerait rien pendant le geste (l'image
 * remplit sa boîte au pixel près, la déplacer ne bouge rien) : on sert donc
 * l'image ENTIÈRE à l'échelle de couverture, et c'est `object-position` qui
 * la fait glisser dans le cadre. Le rendu publié, lui, reste découpé par le
 * serveur — mêmes pixels visibles, sans le débord téléchargé.
 *
 * 0 quand les dimensions de la source sont inconnues : il n'y a alors rien à
 * calculer, l'aperçu retombe sur la découpe du serveur (le cadrage n'apparaît
 * qu'au relâchement).
 */
export const coverWidth = (box, sourceWidth, sourceHeight) => {
  const sw = Number(sourceWidth) || 0
  const sh = Number(sourceHeight) || 0
  if (!box || sw <= 0 || sh <= 0) return 0
  return Math.max(box.w, Math.round(box.h * (sw / sh)))
}

/** `object-fit` induit par le style de cadrage : `s` garde toute l'image. */
export const fitForOp = (op) => (op === 's' ? 'contain' : 'cover')

/**
 * Le style « Contenir » IMPOSE les proportions de la source, et on ne peut pas
 * les délier : le serveur met l'image à l'échelle sans jamais la déformer
 * (`image_scale` garde le rapport), donc une boîte d'un autre rapport ne
 * produirait que des bandes vides autour de l'image. Les deux styles qui
 * DÉCOUPENT (`sc`, `c`), eux, honorent la boîte au pixel près : là, délier a un
 * sens — c'est ainsi qu'on obtient un portrait sur mobile et un panoramique sur
 * ordinateur à partir de la même photo.
 */
export const ratioForcedByOp = (op) => op === 's'

/**
 * Redimensionner à la main garde les proportions PAR DÉFAUT — c'est ce qui
 * évite de déformer une image sans le vouloir. Le bouton « Délier » de
 * l'éditeur écrit `lockRatio: false` pour permettre le recadrage libre.
 */
export const isRatioLocked = (sizing) => {
  const normalized = normalizeSizing(sizing)
  const op = normalized?.op ?? DEFAULT_OP
  if (ratioForcedByOp(op)) return true
  return typeof normalized?.lockRatio === 'boolean' ? normalized.lockRatio : true
}

/** Le rapport de forme de l'ORIGINAL, quand la valeur le porte. 0 sinon. */
export const sourceRatio = (value) => {
  const w = Number(value?.width)
  const h = Number(value?.height)
  return w > 0 && h > 0 ? w / h : 0
}

/**
 * Une boîte ramenée à un rapport donné, en gardant sa largeur. Le côté DÉDUIT
 * est calé sur le pas du quantizer : sans ça l'éditeur afficherait 772 quand
 * l'URL demandera 780 — un chiffre qui ne correspond à rien de servi.
 */
export const conformBox = (box, wantedRatio) => {
  const normalized = normalizeBox(box)
  if (!normalized || !wantedRatio) return normalized
  return normalizeBox({ w: normalized.w, h: snap(normalized.w / wantedRatio) })
}

/**
 * Toutes les bandes ramenées au rapport de la source. Appelé quand on choisit
 * « Contenir » : sans ça, la boîte réglée d'avant laisserait des bandes vides.
 */
export const conformToRatio = (sizing, wantedRatio) => {
  const normalized = normalizeSizing(sizing)
  if (!normalized || !wantedRatio) return normalized
  let next = normalized
  for (const device of RESPONSIVE_DEVICES) {
    if (normalized[device]) next = setBox(next, device, conformBox(normalized[device], wantedRatio))
  }
  return next
}

const ratio = (box) => (box ? box.w / box.h : 0)

/**
 * Les rapports de forme diffèrent-ils d'une bande à l'autre ? C'est la question
 * qui décide du balisage : à rapport constant un `<img>` + `srcset` suffit (une
 * seule forme, plusieurs largeurs) ; dès qu'il varie, seul un `<picture>` peut
 * servir une découpe différente par bande.
 */
export const ratiosDiffer = (boxes) => {
  if (!boxes) return false
  const values = RESPONSIVE_DEVICES.map(d => ratio(boxes[d])).filter(Boolean)
  if (values.length < 2) return false
  return values.some(v => Math.abs(v - values[0]) > 0.01)
}

/**
 * Les variables CSS posées en style en ligne sur l'image : UNE PAIRE PAR BANDE,
 * et rien d'autre.
 *
 * Le choix de la bande se fait dans la recette (`.builder-img-sized`
 * d'index.css), qui redéclare `width`/`aspect-ratio` sous chaque `@container` —
 * jamais par une variable intermédiaire que les requêtes réaffecteraient. Un
 * style EN LIGNE bat toute feuille de style : poser un `--bimg-w` en ligne
 * rendait les règles de bande inopérantes, et l'image gardait sa boîte desktop
 * sur mobile (le glissé peignait alors toutes les bandes à l'identique, ce qui
 * masquait le défaut à l'écran).
 *
 * Hors d'un conteneur de requêtes (aperçu du catalogue), c'est la déclaration de
 * base qui s'applique, donc la bande desktop.
 */
export const sizingVars = (sizing) => {
  const boxes = resolvedBoxes(sizing)
  if (!boxes) return null
  const normalized = normalizeSizing(sizing)
  const vars = { '--bimg-fit': fitForOp(normalized.op) }
  for (const device of RESPONSIVE_DEVICES) {
    vars[`--bimg-w-${device}`] = `${boxes[device].w}px`
    vars[`--bimg-ar-${device}`] = `${boxes[device].w} / ${boxes[device].h}`
  }
  return vars
}

/**
 * L'attribut `sizes` d'un rendu à une seule source : la largeur RÉELLE de la
 * boîte par bande. Un `vw` brut mentirait dès que l'image est capée par sa
 * colonne — ici la largeur est celle que l'éditeur a réglée à l'écran.
 */
export const sizingSizes = (boxes) => {
  if (!boxes) return undefined
  const parts = SIZING_BANDS.map(({ device, media }) => `${media} ${boxes[device].w}px`)
  parts.push(`${boxes.desktop.w}px`)
  return parts.join(', ')
}

/**
 * L'échelle de largeurs d'une boîte : 1× et 2× (écrans à haute densité),
 * quantifiées et PLAFONNÉES à la résolution de la source — recadrer ne fait que
 * réduire, demander plus large ne rend pas plus net, seulement plus lourd.
 */
export const boxWidths = (box, sourceWidth = 0) => {
  if (!box) return undefined
  const cap = quantize(sourceWidth)
  const out = []
  for (const w of [quantize(box.w), quantize(box.w * 2)]) {
    const capped = cap ? Math.min(w, cap) : w
    if (capped > 0 && !out.includes(capped)) out.push(capped)
  }
  return out.length ? out : undefined
}

/**
 * L'échelle d'un rendu à une seule source : l'union des trois bandes. Le
 * navigateur choisit dedans avec `sizes` ; une bande absente de l'échelle le
 * ferait arrondir au cran du dessus, c'est-à-dire télécharger trop.
 */
export const unionWidths = (boxes, sourceWidth = 0) => {
  if (!boxes) return undefined
  const all = new Set()
  for (const device of RESPONSIVE_DEVICES) {
    for (const w of (boxWidths(boxes[device], sourceWidth) ?? [])) all.add(w)
  }
  return all.size ? Array.from(all).sort((a, b) => a - b) : undefined
}

// --- écritures (poignée + éditeur) ------------------------------------------

/** `sizing` avec la boîte d'UNE bande remplacée. */
export const setBox = (sizing, device, box) => {
  const next = { ...(sizing && typeof sizing === 'object' ? sizing : {}) }
  const normalized = normalizeBox(box)
  if (normalized) next[device] = normalized
  else delete next[device]
  if (!next.op) next.op = DEFAULT_OP
  return normalizeSizing(next)
}

/** `sizing` avec LA MÊME boîte sur les trois bandes. */
export const applyBoxToAll = (sizing, box) => {
  let next = sizing
  for (const device of RESPONSIVE_DEVICES) next = setBox(next, device, box)
  return next
}

/** `sizing` sans la bande donnée (elle repart en héritage). */
export const clearBox = (sizing, device) => setBox(sizing, device, null)

/** `sizing` avec un autre style de cadrage / format de sortie. */
export const setSizingOption = (sizing, key, value) => {
  const next = { ...(sizing && typeof sizing === 'object' ? sizing : {}) }
  next[key] = value
  return normalizeSizing(next)
}

/**
 * Pose (ou retire) `sizing` dans une valeur d'image sans toucher au reste :
 * `mid`/`alt` sont la référence au média, ils ne bougent jamais ici.
 */
export const withSizing = (value, sizing) => {
  // Une valeur qui n'est pas un objet est une URL nue (forme héritée) : la
  // remplacer par `{sizing}` perdrait l'image, sans erreur. On la garde sous la
  // clé que `LazyImage.resolveSource` sait lire.
  const base = value && typeof value === 'object'
    ? { ...value }
    : (typeof value === 'string' && value ? { url: value } : {})
  const normalized = normalizeSizing(sizing)
  if (normalized) base.sizing = normalized
  else delete base.sizing
  return base
}

/** Un cran de poignée : arrondi au pas, borné. */
export const snap = (value) => Math.min(
  MAX_BOX,
  Math.max(MIN_BOX, Math.round(Number(value) / SIZING_STEP) * SIZING_STEP),
)
